// @ts-strict-ignore
"use client";
import React, { useMemo, useRef, useEffect, useCallback } from "react";
import { useState } from "react";
import type { ImageProps } from "next/image";
import Image from "next/image";

interface NextImageProps extends ImageProps {
  userSetImageSize?: {
    width: number;
    height: number;
  };
  containerStyles?: React.CSSProperties;
}

const NextImage = (props: NextImageProps) => {
  const userSetImageSize = useMemo(() => {
    return (
      props.userSetImageSize || {
        width: 600,
        height: 400
      }
    );
  }, [props.userSetImageSize]);

  const [imageSize, setImageSize] = useState(userSetImageSize);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateAspectRatio = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      if (containerWidth === 0 || imageSize.width === 0) {
        // Handle division by zero or invalid calculation
        return;
      }
      const aspectRatio = (imageSize.height / imageSize.width) * 100; // Calculate aspect ratio in percentage
      const calculatedHeight = (containerWidth * aspectRatio) / 100;
      setImageSize({
        width: containerWidth,
        height: calculatedHeight
      });
    }
  }, [imageSize.height, imageSize.width]);

  useEffect(() => {
    if (!props.height && !props.width) {
      calculateAspectRatio();
      window.addEventListener("resize", calculateAspectRatio);
    }
    return () => {
      window.removeEventListener("resize", calculateAspectRatio);
    };
  }, [props.height, props.width, calculateAspectRatio]);

  if ((props.height && props.width) || props.fill) {
    return <Image alt={props.alt} quality={100} {...props} />;
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100%", // Ensure the image doesn't exceed its parent's width
        ...(props.containerStyles ?? {})
      }}
    >
      <Image
        alt={props.alt}
        onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
          const target = e.target as HTMLImageElement;

          if (!props.userSetImageSize) {
            setImageSize({
              width: target.naturalWidth,
              height: target.naturalHeight
            });
          }
        }}
        width={imageSize.width}
        height={imageSize.height}
        quality={100}
        {...props}
      />
    </div>
  );
};

export default NextImage;
